
import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import SecurityHeaders from "@/components/SecurityHeaders";
import NetworkStatus from "@/components/NetworkStatus";

// Eagerly loaded core pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy loaded pages
const Events = lazy(() => import("./pages/Events"));
const CreateEvent = lazy(() => import("./pages/CreateEvent"));
const EditEvent = lazy(() => import("./pages/EditEvent"));
const EventDetails = lazy(() => import("./pages/EventDetails"));
const Admin = lazy(() => import("./pages/Admin"));
const Profile = lazy(() => import("./pages/Profile"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TwoFactorVerification = lazy(() => import("./pages/TwoFactorVerification"));
const Contacts = lazy(() => import("./pages/Contacts"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const Gifts = lazy(() => import("./pages/Gifts"));
const AuctionPortal = lazy(() => import("./components/auction/public/AuctionPortal"));
const PublicEvent = lazy(() => import("./pages/PublicEvent"));
const PublicEventByCode = lazy(() => import("./pages/PublicEventByCode"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCanceled = lazy(() => import("./pages/PaymentCanceled"));
const MobileCheckout = lazy(() => import("./pages/MobileCheckout").then(m => ({ default: m.MobileCheckout })));
const MobileRegistration = lazy(() => import("./pages/MobileRegistration").then(m => ({ default: m.MobileRegistration })));
const AuctionItemPrintout = lazy(() => import("./pages/AuctionItemPrintout"));
const AuctionConfirm = lazy(() => import("./pages/AuctionConfirm").then(m => ({ default: m.AuctionConfirm })));
const TableCompetitionLeaderboard = lazy(() => import("./pages/TableCompetitionLeaderboard"));
const PublicGiftReceipt = lazy(() => import("./pages/PublicGiftReceipt"));
const SuperAdmin = lazy(() => import("./pages/SuperAdmin"));
const SponsorshipSuccess = lazy(() => import("./pages/SponsorshipSuccess"));
const SponsorshipCancel = lazy(() => import("./pages/SponsorshipCancel"));
const PublicSponsorships = lazy(() => import("./pages/PublicSponsorships"));
const PublicSponsorshipCheckout = lazy(() => import("./pages/PublicSponsorshipCheckout"));
const AdminPayments = lazy(() => import("./pages/AdminPayments"));
const ConnectCallback = lazy(() => import("./pages/ConnectCallback"));

function App() {
  return (
    <>
      <SecurityHeaders />
      <NetworkStatus />
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
        <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-2fa" element={<TwoFactorVerification />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-canceled" element={<PaymentCanceled />} />
        <Route path="/checkout/:token" element={<MobileCheckout />} />
        <Route path="/register/:token" element={<MobileRegistration />} />
        <Route path="/auction-item-printout/:itemId" element={<ProtectedRoute><AuctionItemPrintout /></ProtectedRoute>} />
        <Route path="/event/:code/:slug/sponsorships/:packageId/checkout" element={<PublicSponsorshipCheckout />} />
        <Route path="/event/:code/sponsorships/:packageId/checkout" element={<PublicSponsorshipCheckout />} />
        <Route path="/event/:code/:slug/sponsorships" element={<PublicSponsorships />} />
        <Route path="/event/:code/sponsorships" element={<PublicSponsorships />} />
        <Route path="/event/:code/:slug" element={<PublicEventByCode />} />
        <Route path="/event/:code" element={<PublicEventByCode />} />
        <Route path="/event/:slug" element={<PublicEvent />} />
        <Route path="/leaderboard/:code/:slug" element={<ProtectedRoute><TableCompetitionLeaderboard /></ProtectedRoute>} />
        <Route path="/leaderboard/:code" element={<ProtectedRoute><TableCompetitionLeaderboard /></ProtectedRoute>} />
        <Route path="/leaderboard/:slug" element={<ProtectedRoute><TableCompetitionLeaderboard /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
        <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
        <Route path="/events/create" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
        <Route path="/events/:id/edit" element={<ProtectedRoute><EditEvent /></ProtectedRoute>} />
        <Route path="/events/:id/auction" element={<AuctionPortal />} />
        <Route path="/event/:slug/auction" element={<AuctionPortal />} />
        <Route path="/auction/:code/:slug" element={<AuctionPortal />} />
        <Route path="/auction/:code" element={<AuctionPortal />} />
        <Route path="/events/:id" element={<ProtectedRoute><EventDetails /></ProtectedRoute>} />
              <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
              <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
              <Route path="/gifts" element={<ProtectedRoute><Gifts /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
              <Route path="/admin/payments" element={<ProtectedRoute><AdminPayments /></ProtectedRoute>} />
              <Route path="/admin/payments/connect/callback" element={<ProtectedRoute><ConnectCallback /></ProtectedRoute>} />
              <Route path="/super-admin" element={<ProtectedRoute><SuperAdmin /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/auction/confirm" element={<AuctionConfirm />} />
        <Route path="/receipt/:token" element={<PublicGiftReceipt />} />
        <Route path="/sponsorship/success" element={<SponsorshipSuccess />} />
        <Route path="/sponsorship/cancel" element={<SponsorshipCancel />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;